import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type ILayerProps, type ITextLayerProps, type TextEffectKind } from '@/version-types';
import { pushHistory, requestTextSVGPreview, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

type EffectItem<K extends TextEffectKind> = ITextLayerProps[K][number];

export type EffectManagerOptions<K extends TextEffectKind> = {
    key: K;
    funcID: string;
    maxCount: number;
    create: (data: ITextLayerProps, color?: string) => EffectItem<K>;
    refreshShapeText?: boolean;
    afterAdd?: (layer: ILayerProps) => ILayerProps;
};

export class EffectManager<K extends TextEffectKind> {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private options: EffectManagerOptions<K>,
        private shapeText?: IShapeText,
    ) {}

    private getEffectItems(layer: ILayerProps): EffectItem<K>[] {
        const data = layer.data as ITextLayerProps;
        return (data[this.options.key] || []) as EffectItem<K>[];
    }

    private setEffectItems(layer: ILayerProps, items: EffectItem<K>[]) {
        const data = layer.data as ITextLayerProps;
        return {
            ...layer,
            data: {
                ...data,
                [this.options.key]: items,
            },
        } as ILayerProps;
    }

    private async refreshShapeTextAsNeeded(layer: ILayerProps) {
        if (!this.options.refreshShapeText || !this.shapeText || !get(layer, 'data.shapeExtension', null)) return layer;
        return (await this.shapeText.refreshShapeTextRender(layer)) || layer;
    }

    private commitGroup(
        prevLayer: ILayerProps,
        children: ILayerProps[],
        funcID?: string,
        writeHistory = true,
        previewPhase?: 'update' | 'commit',
    ) {
        const nextLayer: ILayerProps = { ...prevLayer, children };

        if (previewPhase) requestTextSVGPreview(this.framework, previewPhase, nextLayer);
        updateLayer(this.framework, nextLayer, {
            activate: true,
            dataOnly: previewPhase === 'update',
        });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, funcID);

        return nextLayer;
    }

    async add(layerID: string, color?: string) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps) => {
            const layerData = layer.data as ITextLayerProps;
            const items = this.getEffectItems(layer);
            if (items.length === this.options.maxCount) return layer;

            const nextItems =
                this.options.maxCount === 1
                    ? [this.options.create(layerData, color)]
                    : [...items, this.options.create(layerData, color)];
            const nextLayer = this.setEffectItems(layer, nextItems);
            return this.options.afterAdd?.(nextLayer) || nextLayer;
        };

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.commitGroup(prevLayer, prevLayer.children.map(prepare), this.options.funcID);
        }

        let nextLayer = prepare(prevLayer);
        nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, this.options.funcID);
        return nextLayer;
    }

    async remove(layerID: string, index?: number) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps) => {
            const items = this.getEffectItems(layer);
            if (items.length === 0 || (index !== undefined && index >= items.length)) return layer;
            return this.setEffectItems(
                layer,
                items.filter((_, i) => (index === undefined ? false : i !== index)),
            );
        };

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.commitGroup(prevLayer, prevLayer.children.map(prepare), this.options.funcID);
        }

        let nextLayer = prepare(prevLayer);
        nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, this.options.funcID);
        return nextLayer;
    }

    async setVisibility(layerID: string, flag: boolean, index?: number) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps) => {
            const items = this.getEffectItems(layer);
            if (items.length === 0 || (index !== undefined && index >= items.length)) return layer;
            return this.setEffectItems(
                layer,
                items.map((item, i) => (i === index || index === undefined ? { ...item, hidden: flag } : item)),
            );
        };

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.commitGroup(
                prevLayer,
                prevLayer.children.map(prepare),
                trackConfig[LayerType.TEXT_LAYER].effects,
            );
        }

        let nextLayer = prepare(prevLayer);
        nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);
        return nextLayer;
    }

    async adjust(
        layerID: string,
        payload: { idx: number; data: Partial<EffectItem<K>> },
        options?: { writeHistory?: boolean },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer && this.prevLayer.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const prepare = (layer: ILayerProps) => {
            const items = this.getEffectItems(layer);
            if (items.length === 0 || payload.idx >= items.length) return layer;
            return this.setEffectItems(
                layer,
                items.map((item, i) => (i === payload.idx ? { ...item, ...payload.data } : item)),
            );
        };
        const { writeHistory = false } = options || {};

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const nextLayer = this.commitGroup(
                prevLayer,
                prevLayer.children.map(prepare),
                trackConfig[LayerType.TEXT_LAYER].effects,
                writeHistory,
                writeHistory ? 'commit' : 'update',
            );
            if (writeHistory) this.prevLayer = null;
            return nextLayer;
        }

        let nextLayer = prepare(prevLayer);
        if (writeHistory) nextLayer = await this.refreshShapeTextAsNeeded(nextLayer);

        const hasShapeExtension = Boolean((prevLayer.data as ITextLayerProps).shapeExtension);
        if (writeHistory) requestTextSVGPreview(this.framework, 'commit', nextLayer);
        else if (!hasShapeExtension) requestTextSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !hasShapeExtension && !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);
            this.prevLayer = null;
        }
        return nextLayer;
    }
}
